/**
 * Canonical Amazon affiliate primitives — the single source of truth for the
 * Associates tag and the product-link format. Runtime code (app/, components/,
 * lib/, data/) must import from here instead of hardcoding either one; a guard
 * test (__tests__/lib/affiliate.test.ts) enforces this. Historical
 * one-off scripts under scripts/ are exempt by design.
 *
 * Field ownership: ASIN is the shared key across every product data source.
 * PAAPI is the authority for current price and product title (synced by the
 * weekly paapi:refresh workflow). Static data files and the Directus
 * `products` collection own editorial copy (descriptions, badges, reasons).
 * A price or title in a data file is a cache of PAAPI — never hand-edit one.
 */

export const AMAZON_AFFILIATE_TAG = 'whatgrassis0b-20'

/** B-prefixed ASIN or legacy 10-digit ISBN. */
const ASIN_SHAPE = /^(?:B[0-9A-Z]{9}|[0-9]{9}[0-9X])$/

export function isValidAsin(asin: string): boolean {
  return ASIN_SHAPE.test(asin)
}

/**
 * Canonical affiliate product URL. Throws on a malformed ASIN so a typo in a
 * data file fails the build instead of shipping a dead $0 link.
 */
export function amazonUrl(asin: string): string {
  if (!ASIN_SHAPE.test(asin)) {
    throw new Error(`amazonUrl: "${asin}" is not a valid ASIN`)
  }
  return `https://www.amazon.com/dp/${asin}/ref=nosim?tag=${AMAZON_AFFILIATE_TAG}`
}

/** Tagged Amazon search URL — for products without a stable single ASIN. */
export function amazonSearchUrl(query: string): string {
  return `https://www.amazon.com/s?k=${encodeURIComponent(query)}&tag=${AMAZON_AFFILIATE_TAG}`
}
